import { LogManager } from './Logger';

const logger = LogManager.getLogger('speakerIdentification');

interface SpeakerSegment {
  speaker: string;
  content: string;
}

interface TurnSegment {
  speaker: string;
  content: string;
  charLength: number;
  selfIntroIndex: number | null;
  thirdPersonIntroIndex: number | null;
  index: number;
}

function getSelfIntroRegex() {
  return /\b(i'm|i am|my name is)\b/i;
}

function getThirdPersonIntroRegex() {
  return /(joined by|with us|today we have|here with|i'm joined by|i am joined by|to introduce)/i;
}

function findFirstIndex(text: string, regex: RegExp): number | null {
  const re = new RegExp(regex.source, regex.flags);
  const m = re.exec(text);
  return m ? m.index : null;
}

function findLastIndex(text: string, regex: RegExp): number | null {
  // Make sure the regex is global so we can iterate through all matches
  const re = new RegExp(
    regex.source,
    regex.flags.includes('g') ? regex.flags : regex.flags + 'g',
  );

  let lastIndex: number | null = null;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    lastIndex = match.index;
  }

  return lastIndex;
}

function windowSpeakerSegment(
  text: string,
  center: number,
  leftPad: number,
  rightPad: number,
): string {
  const start = Math.max(0, center - leftPad);
  const end = Math.min(text.length, center + rightPad);
  let headerPrefix = '';
  let prefix = '';
  if (start > 0) {
    const speakerHeaderMatch = text.match(/^\s*(Speaker\s+\d+:)/i);
    if (speakerHeaderMatch) {
      headerPrefix = `${speakerHeaderMatch[1]} `;
    } else {
      const colonIdx = text.indexOf(':');
      if (colonIdx > -1 && colonIdx < 50) {
        headerPrefix = `${text.slice(0, colonIdx + 1)} `;
      }
    }
    prefix = `${headerPrefix}…`;
  }
  const suffix = end < text.length ? '…' : '';
  return `${prefix}${text.slice(start, end)}${suffix}`;
}

/**
 * Build overlapping transcript chunks at speaker boundaries.
 * Extraction of the inline logic from identifySpeakers for clarity & reuse.
 * Strategy:
 *  - Grow a chunk until target char size OR speaker count threshold reached.
 *  - Avoid emitting tiny (< minNonFinalChunkChars) non-final chunks to reduce low-signal passes.
 *  - Overlap last N speaker segments to preserve identity continuity across chunk boundaries.
 */
export function buildOverlappingChunks(
  speakerSegments: {
    speaker: string;
    content: string;
    startIndex: number;
    endIndex: number;
  }[],
  options: {
    targetChunkSize: number;
    maxChunks: number;
    speakersPerChunk?: number;
    overlapSpeakers?: number;
    minChunkChars?: number; // absolute minimum to keep (after trim)
    minNonFinalChunkChars?: number; // threshold to suppress very small non-final chunk
    createThirdPersonSupplementalChunks?: boolean; // add extra chunks to cover third-person intros
    supplementalThirdPersonMax?: number; // limit the number of supplemental chunks
    capMultiplier?: number;
  },
): string[] {
  const {
    targetChunkSize,
    maxChunks,
    speakersPerChunk = 10,
    overlapSpeakers = 2,
    minChunkChars = 100,
    minNonFinalChunkChars = 500,
    createThirdPersonSupplementalChunks = true,
    supplementalThirdPersonMax = 3,
    capMultiplier = 1.5,
  } = options;
  const thirdRe = getThirdPersonIntroRegex();

  const chunks: string[] = [];
  const chunkRanges: Array<{ start: number; end: number }> = [];
  let currentChunkStartIdx = 0;
  // Hard cap to prevent any single chunk from becoming overwhelmingly large
  const capLimit = Math.floor(targetChunkSize * capMultiplier);

  // Pre-scan: detect third-person intro indices
  const thirdPersonIntroIndexes: number[] = [];
  for (let i = 0; i < speakerSegments.length - 1; i++) {
    if (findFirstIndex(speakerSegments[i].content, thirdRe) !== null) {
      thirdPersonIntroIndexes.push(i);
    }
  }

  while (
    currentChunkStartIdx < speakerSegments.length &&
    chunks.length < maxChunks
  ) {
    let currentChunkEndIdx = currentChunkStartIdx;
    let currentChunkSize = 0;

    // Grow chunk
    while (currentChunkEndIdx < speakerSegments.length) {
      currentChunkSize += speakerSegments[currentChunkEndIdx].content.length;
      currentChunkEndIdx++;

      // If we exceeded the hard cap, stop growing this chunk now.
      // We'll truncate the last segment's content when constructing chunk text below.
      if (currentChunkSize >= capLimit) {
        logger.warn(
          `Chunk reached cap limit (${capLimit} chars). Will truncate last segment content to fit.`,
        );
        break;
      }

      const speakersInChunk = currentChunkEndIdx - currentChunkStartIdx;
      const shouldEndChunk =
        currentChunkSize >= targetChunkSize ||
        speakersInChunk >= speakersPerChunk ||
        currentChunkEndIdx === speakerSegments.length;

      if (shouldEndChunk) {
        // Prevent emitting very small non-final chunks
        if (
          currentChunkSize > minNonFinalChunkChars ||
          currentChunkEndIdx === speakerSegments.length
        ) {
          break;
        }
      }
    }

    const chunkSegments = speakerSegments.slice(
      currentChunkStartIdx,
      currentChunkEndIdx,
    );
    // Build chunk text; if we exceeded capLimit, only truncate the last segment
    let chunkText = buildCapAwareChunkText(chunkSegments, currentChunkSize);
    if (chunkText.trim().length > minChunkChars) {
      chunks.push(chunkText);
      // Track range for coverage checks (end is exclusive)
      chunkRanges.push({
        start: currentChunkStartIdx,
        end: currentChunkEndIdx,
      });
      logger.info(
        `Chunk ${chunks.length}: ${chunkSegments.length} speakers, ${chunkText.length} chars`,
      );
    }

    if (currentChunkEndIdx < speakerSegments.length) {
      currentChunkStartIdx = Math.max(
        currentChunkStartIdx + 1,
        currentChunkEndIdx - overlapSpeakers,
      );
    } else {
      break;
    }
  }

  // After main chunking, opportunistically add supplemental chunks for uncovered third-person pairs
  addExtraChunksWithThirdPersonIntros();

  if (chunks.length >= maxChunks) {
    logger.warn(
      `Reached maximum chunk limit (${maxChunks}). Transcript may be exceptionally long.`,
    );
  }

  logger.info(`Created ${chunks.length} overlapping chunks for analysis`);
  return chunks;

  function addExtraChunksWithThirdPersonIntros() {
    if (
      createThirdPersonSupplementalChunks &&
      thirdPersonIntroIndexes.length > 0
    ) {
      let added = 0;
      for (const introIdx of thirdPersonIntroIndexes) {
        if (added >= supplementalThirdPersonMax) break;
        const isAlreadyCovered = chunkRanges.some(
          r => r.start <= introIdx && introIdx + 1 < r.end,
        );
        if (isAlreadyCovered) continue;
        const text = buildChunkBasedOnThirdPersonIntro(introIdx);
        if (text.trim().length > minChunkChars) {
          chunks.push(text);
          added += 1;
        }
      }
      if (added > 0) {
        logger.info(`Supplemental third-person chunks added: ${added}`);
      }
    }
  }

  function buildChunkBasedOnThirdPersonIntro(introSegmentIdx: number): string {
    const mentionSeg = speakerSegments[introSegmentIdx];
    const nextSeg = speakerSegments[introSegmentIdx + 1];
    // we want the closest intro mention to the end
    const center = findLastIndex(mentionSeg.content, thirdRe);
    if (center === null) {
      return '';
    }
    const introContext = windowSpeakerSegment(
      mentionSeg.content,
      center,
      1000,
      2000,
    );
    const introResponse = nextSeg.content.slice(0, 4000);
    let text = `${introContext}${introResponse}`;
    if (text.length > capLimit) {
      text = text.slice(0, Math.max(0, capLimit - 1)) + '…';
    }
    return text;
  }

  function buildCapAwareChunkText(
    chunkSegments: {
      speaker: string;
      content: string;
      startIndex: number;
      endIndex: number;
    }[],
    currentChunkSize: number,
  ): string {
    let chunkText = '';
    if (chunkSegments.length > 0) {
      const lastSeg = chunkSegments[chunkSegments.length - 1];
      // Sometimes the last segment makes the chunk grow too much, exceeding the cap limit.
      if (currentChunkSize > capLimit) {
        const prevText = chunkSegments
          .slice(0, -1)
          .map(seg => seg.content)
          .join('');
        const space = capLimit - prevText.length; // space available for last segment
        if (space <= 0) {
          // No space left for the last segment; if prevText already exceeds cap, truncate it to cap
          if (prevText.length >= capLimit) {
            chunkText = prevText.slice(0, Math.max(0, capLimit - 1)) + '…';
          } else {
            // PrevText fits; keep it and indicate truncation
            chunkText = prevText + '…';
          }
        } else {
          // Take as much as fits; if truncated, append an ellipsis
          const allowed = Math.min(space, lastSeg.content.length);
          const tail = lastSeg.content.slice(0, allowed);
          const truncated = allowed < lastSeg.content.length;
          chunkText = prevText + tail + (truncated ? '…' : '');
        }
      } else {
        // Within cap, include full contents
        chunkText = chunkSegments.map(seg => seg.content).join('');
      }
    }
    return chunkText;
  }
}

/**
 * Parse a full transcript into speaker-labeled segments.
 * Returns empty array if formatting not detected (caller can fallback to single-pass path).
 */
export function extractSpeakerSegments(fullTranscript: string): {
  speaker: string;
  content: string;
  startIndex: number;
  endIndex: number;
}[] {
  const speakerPattern = /^([^:]+):\s*/gm;
  const segments: {
    speaker: string;
    content: string;
    startIndex: number;
    endIndex: number;
  }[] = [];
  let lastIndex = 0;
  let currentSpeaker = '';
  let match: RegExpExecArray | null;

  const hasSpeakerFormat = speakerPattern.test(fullTranscript);
  speakerPattern.lastIndex = 0;
  if (!hasSpeakerFormat) {
    logger.info(
      'Transcript does not have speaker formatting, using single-pass approach',
    );
    return [];
  }

  while ((match = speakerPattern.exec(fullTranscript)) !== null) {
    const potentialSpeaker = match[1].trim();
    const isSpeaker =
      potentialSpeaker.includes('Speaker') ||
      potentialSpeaker.includes('spk_') ||
      (potentialSpeaker.length < 50 && !potentialSpeaker.includes('\n'));
    if (isSpeaker) {
      if (currentSpeaker) {
        const segmentContent = fullTranscript.substring(lastIndex, match.index);
        if (segmentContent.trim()) {
          segments.push({
            speaker: currentSpeaker,
            content: segmentContent,
            startIndex: lastIndex,
            endIndex: match.index,
          });
        }
      }
      currentSpeaker = potentialSpeaker;
      lastIndex = match.index;
    }
  }

  if (currentSpeaker && lastIndex < fullTranscript.length) {
    const segmentContent = fullTranscript.substring(lastIndex);
    if (segmentContent.trim()) {
      segments.push({
        speaker: currentSpeaker,
        content: segmentContent,
        startIndex: lastIndex,
        endIndex: fullTranscript.length,
      });
    }
  }

  if (segments.length === 0) {
    logger.info('No speaker segments found, using single-pass approach');
    return [];
  }

  logger.info(`Found ${segments.length} speaker segments`);
  return segments;
}

export function buildRosterTextForAnchors(
  speakerSegments: SpeakerSegment[],
  fullTranscript: string,
): string {
  const selfIntroRegex = getSelfIntroRegex();
  const thirdPersonIntroRegex = getThirdPersonIntroRegex();

  const turns: TurnSegment[] = speakerSegments.map((seg, idx) => {
    const text = seg.content;
    return {
      speaker: seg.speaker,
      content: text,
      charLength: text.length,
      selfIntroIndex: findFirstIndex(text, selfIntroRegex),
      thirdPersonIntroIndex: findFirstIndex(text, thirdPersonIntroRegex),
      index: idx,
    };
  });
  const TARGET_ROSTER_CHARS = 2000;
  const seenSpeakers = new Set<string>();
  const roster: TurnSegment[] = [];
  const priorityTurns = [
    ...turns.filter(t => t.selfIntroIndex !== null),
    ...turns.filter(
      t => t.selfIntroIndex === null && t.thirdPersonIntroIndex !== null,
    ),
    ...turns,
  ];

  for (const t of priorityTurns) {
    const isOverLimit =
      roster.reduce((sum, x) => sum + x.charLength, 0) > TARGET_ROSTER_CHARS;
    if (isOverLimit) break;
    let effectiveSpeaker = t.speaker;
    let snippet: string;
    if (t.selfIntroIndex !== null) {
      // Center around the self-introduction phrase
      snippet = windowSpeakerSegment(t.content, t.selfIntroIndex, 120, 260);
    } else if (t.thirdPersonIntroIndex !== null) {
      // Include context around the third-person mention and the next speaker's opening
      const hasNextTurn = t.index + 1 < turns.length;
      if (!hasNextTurn) {
        continue;
      }
      const nextTurn = turns[t.index + 1];
      const introContext = windowSpeakerSegment(
        t.content,
        t.thirdPersonIntroIndex,
        100,
        140,
      );
      const nextStart = nextTurn.content.slice(0, 140);
      snippet = `${introContext}${nextStart}`;
      effectiveSpeaker = nextTurn.speaker;
    } else {
      // Fallback to the beginning of the turn as before
      snippet =
        t.content.length > 400 ? t.content.slice(0, 380) + '…' : t.content;
    }
    snippet = snippet.trim();

    // Avoid duplicate speakers (dedupe by effective speaker)
    if (seenSpeakers.has(effectiveSpeaker)) continue;
    seenSpeakers.add(effectiveSpeaker);
    roster.push({
      ...t,
      speaker: effectiveSpeaker,
      content: snippet,
      charLength: snippet.length,
    });
  }
  let rosterText = roster.map(r => r.content).join('\n\n');
  if (rosterText.length < 500) {
    rosterText = fullTranscript.slice(0, 2000);
  }
  return rosterText;
}
